// src/demo/DemoLaunchAdminCard.jsx
//
// Admin-only Settings → Account card that re-enters Demo Mode for
// the calling user. POSTs /api/demo-mode/enter (which is server-side
// gated to the platform admin email) then fires the cross-component
// 'demo-mode-changed' event so useDemoModeFlag re-queries and the
// shell mounts without a hard reload.
//
// Useful for screenshots, prospect walkthroughs, and verifying that
// the seal + seed are behaving across screens. Hidden for non-admin
// roles so customers don't accidentally flip their own account into
// demo and wonder where their data went.

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { supabase } from '../lib/supabase';
import { notifyDemoModeChanged } from './useDemoModeFlag';

export default function DemoLaunchAdminCard({ currentUser, showToast = () => {} }) {
  const [busy, setBusy] = React.useState(false);

  // Defense in depth — the server is the real gate, this just keeps
  // the button off non-admin screens.
  if (currentUser?.role !== 'admin') return null;

  async function launch() {
    if (busy) return;
    setBusy(true);
    try {
      const jwt = readPersistedJwtDirect();
      if (!jwt) throw new Error('not signed in');
      const res = await fetch('/api/demo-mode/enter', {
        method:  'POST',
        headers: { authorization: `Bearer ${jwt}` },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || `http_${res.status}`);
      notifyDemoModeChanged();
      showToast('🎬 Demo Mode launched — DEMO badge + tour overlaying your account.');
    } catch (err) {
      showToast(`Couldn't launch demo: ${err.message || err}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bg-white border border-violet-200 rounded-xl shadow-sm p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-base font-bold text-slate-900">Demo Mode (admin)</h3>
            <span className="text-[10px] font-bold uppercase tracking-wider bg-violet-100 text-violet-700 border border-violet-200 rounded px-1.5 py-0.5">Admin only</span>
          </div>
          <p className="text-sm text-slate-600 mt-1 leading-relaxed">
            Flip your own account into the fully-loaded demo workspace. The DEMO badge, welcome modal, guided tour, per-tab "why this matters" strip, and live activity feed appear over your real screens. No emails, SMS, dials, or audit writes are produced from a demo session — the boundary seal blocks every external client.
          </p>
        </div>
        <button
          type="button"
          onClick={launch}
          disabled={busy}
          className="bg-violet-600 hover:bg-violet-700 disabled:bg-violet-300 text-white font-bold text-sm px-4 py-2 rounded-md whitespace-nowrap"
        >
          {busy ? 'Launching…' : '🎬 Launch Demo'}
        </button>
      </div>
      <p className="text-xs text-slate-500 mt-3">
        Exit from the violet DEMO badge at the top of any screen. Your real (empty) workspace + brand settings are preserved on exit.
      </p>
    </div>
  );
}
